import { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { Collapse } from "react-collapse";
import "./FirePredictions.css";
import IconButton from "@mui/material/IconButton";
import DomainIcon from "@mui/icons-material/Domain";
import DomainDisabledIcon from "@mui/icons-material/DomainDisabled";
import BarChartIcon from "@mui/icons-material/BarChart";
import CircularProgress from "@mui/material/CircularProgress";
import { LOADING_MESSAGES } from "../../../constants/message";
import ExportDropdown from "../exportDropdown/ExportDropdown";
import { getPredictionColors } from "../../../constants/colors";
import StatisticsModal from "../statisticsModal/StatisticsModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
import { useTranslation } from "react-i18next";

const FirePredictions = ({
  openSection,
  geoJsonResults,
  geoJsonInputContour,
  toggleContourBuildingsVisibility,
  toggleContourVisibility,
  showPregressBar,
  handleExport,
  opacities,
  onHandleOpacity,
  modelErrorMessage,
  handleReset,
  toggleFirstSection,
  setActiveTab,
  onShowUserComponent,
  onShowUserHistoricEvents,
  currentLoadingMessage,
  setCurrentLoadingMessage,
  buildingsLoading,
  predictionHours,
  timeStep,
  onShowAllBuildings,
  onHideAllBuildings,
  isLightMode,
  isSmallScreen,
  onToggleSidebar,
}) => {
    const { t } = useTranslation();
  const [contours, setContours] = useState([]);
  const [globalOpacity, setGlobalOpacity] = useState(100);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [collapsedHours, setCollapsedHours] = useState({});

  const { canAccessLayer, getAllowedDepartments } = useAccessRights();
  const canAccessHistoric = canAccessLayer("physical_model", "historic");
  const allowedDepartments = useMemo(
    () => getAllowedDepartments("physical_model"),
    [getAllowedDepartments],
  );
  const dtMinutesMap = { "1h": 60, "30min": 30, "45min": 45, "15min": 15 };
  const dtMinutes = dtMinutesMap[timeStep] || 60;
  const dtHours = dtMinutes / 60;
  const expectedContourCount = Math.ceil(predictionHours / dtHours);
  const predictionColors = getPredictionColors(
    predictionHours,
    timeStep,
    geoJsonResults.length,
  );

  const reinitializeOperation = () => {
    handleReset();
    toggleFirstSection();
  };

  const formatContourLabel = (index, dtMin) => {
    const totalMinutes = index * dtMin;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes === 0) return `t+${hours}h`;
    if (hours === 0) return `t+${minutes}min`;
    return `t+${hours}h${minutes}min`;
  };

  const setAllContoursOpacity = (value) => {
    const newOpacity = parseInt(value, 10);
    setGlobalOpacity(newOpacity);
    contours.forEach((c) => onHandleOpacity(c.opacityKey, newOpacity));
  };

  const anyBuildingVisible = geoJsonResults.some(
    (r) => r.showBuildings === true,
  );
  const anyBuildingExists = geoJsonResults.some(
    (r) => r.building && r.countBuildings > 0,
  );
  const toggleAllBuildings = () => {
    if (anyBuildingVisible) onHideAllBuildings();
    else onShowAllBuildings();
  };
  const toggleHourCollapse = (hourKey) => {
    setCollapsedHours((prev) => {
      const isCurrentlyCollapsed = prev[hourKey] !== false; // default true if undefined
      return { ...prev, [hourKey]: !isCurrentlyCollapsed };
    });
  };

  // Group contours by hour bucket
  const groupedContours = useMemo(() => {
    const dtMinutesMap = { "1h": 60, "30min": 30, "45min": 45, "15min": 15 };
    const dtMinutes = dtMinutesMap[timeStep] || 60;
    const contoursPerHour = 60 / dtMinutes;

    const groups = [];
    contours.forEach((contour, idx) => {
      const hourIndex = Math.floor(idx / contoursPerHour);
      const hourLabel = `t+${hourIndex + 1}h`;
      if (!groups[hourIndex]) {
        groups[hourIndex] = {
          hourLabel,
          color: contour.color,
          items: [],
          totalSurface: 0,
          totalBuildings: 0,
        };
      }
      groups[hourIndex].items.push(contour);
      groups[hourIndex].totalSurface = contour.surface; // last contour of hour = cumulative
      groups[hourIndex].totalBuildings += contour.buildingscount;
    });

    return groups;
  }, [contours, timeStep]);
  // Loading message rotation
  useEffect(() => {
    if (
      openSection === "predictions" &&
      showPregressBar &&
      !modelErrorMessage
    ) {
      const interval = setInterval(() => {
        setCurrentLoadingMessage((prev) =>
          prev < LOADING_MESSAGES.length - 1 ? prev + 1 : prev,
        );
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [
    openSection,
    showPregressBar,
    modelErrorMessage,
    setCurrentLoadingMessage,
  ]);

  // Build contour list from geoJsonResults
  useEffect(() => {
    if (openSection !== "predictions") return;
    if (modelErrorMessage) {
      setContours([]);
      return;
    }

    const dtMinutesMap = { "1h": 60, "30min": 30, "45min": 45, "15min": 15 };
    const dtMinutes = dtMinutesMap[timeStep] || 60;
    const colors = getPredictionColors(
      predictionHours,
      timeStep,
      geoJsonResults.length,
    );

    const newContours = geoJsonResults
      .map((result, idx) => {
        if (!result?.contour) return null;
        return {
          label: formatContourLabel(idx + 1, dtMinutes),
          color: colors[idx] || "#cc0000",
          surface: ((result.area || 0) / 10000).toFixed(2),
          opacityKey: `geoJsonResult${idx + 1}`,
          buildingscount: result.countBuildings || 0,
          contourIndex: idx, // original index for toggle
        };
      })
      .filter(Boolean);

    setContours(newContours);

    if (
      newContours.length > 0 &&
      opacities[newContours[0]?.opacityKey] !== undefined
    ) {
      setGlobalOpacity(opacities[newContours[0]?.opacityKey]);
    }
  }, [
    geoJsonResults,
    openSection,
    modelErrorMessage,
    predictionHours,
    timeStep,
  ]);

  const allDone =
    contours.length === expectedContourCount && expectedContourCount > 0;

 const handleOpenUserHistoric = () => {
    if (!canAccessHistoric) return;

   // On small screens, close the event-creation sidebar before opening
// user history to prevent the full-width panels from overlapping.
    if (isSmallScreen) {
      onToggleSidebar();
    }

    onShowUserComponent();
    setActiveTab("eventsHistoric");
    onShowUserHistoricEvents();
  };

  return (
    <div>
      <Collapse isOpened={openSection === "predictions"}>
        <div className="predictions-panel">
          {/* ── Error state ──────────────────────────────────────────────── */}
          {modelErrorMessage && (
            <div
              className="error-message"
              style={{ marginTop: "10px" }}
              data-testid="results-prediction-error-message"
            >
              <p
                style={{
                  color: "#b10511",
                  fontWeight: "bold",
                  fontSize: "15px",
                }}
              >
                {modelErrorMessage.type === "permission"
                  ? t("eventCreation.predictions.accessDeniedTitle")
                  : t("eventCreation.predictions.genericError")}
              </p>

              {modelErrorMessage.type === "permission" && (
                <div className="allowed-departments-box">
                  <span className="allowed-departments-label">
                    {t(
                      "eventCreation.predictions.allowedDepartmentsLabel",
                    )}{" "}
                  </span>
                  <div className="allowed-departments-chips">
                    {allowedDepartments.length > 0 ? (
                      allowedDepartments.map((dep) => (
                        <span key={dep} className="allowed-department-chip">
                          {dep}
                        </span>
                      ))
                    ) : (
                      <span className="allowed-department-chip allowed-department-chip--empty">
                        {t("eventCreation.predictions.none")}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <Button
                className="sidebar-primary-btn"
                onClick={reinitializeOperation}
                style={{ marginBottom: "10px" }}
                data-testid="results-reset-button"
              >
                {t("eventCreation.predictions.reset")}
              </Button>
            </div>
          )}
          {/* ── Loading state – no contours yet ──────────────────────────── */}
          {!modelErrorMessage && showPregressBar && contours.length === 0 && (
            <div
              className="fire-loader-wrapper"
              data-testid="event-stepper-progress-bar"
            >
              <div className="fire-loader-header" />
              <div className="fire-loader-bar-track">
                <div className="fire-loader-bar-fill">
                  <div className="fire-loader-bar-shimmer" />
                </div>
                <div className="fire-loader-sparks">
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
              <p className="fire-loader-title">
                                {t(LOADING_MESSAGES[currentLoadingMessage])}
              </p>
              <div className="fire-loader-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {/* ── Results state ─────────────────────────────────────────────── */}
          {!modelErrorMessage && contours.length > 0 && (
            <>
              {/* Global controls */}
              <div className="global-controls">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "var(--text-color)",
                    }}
                  >
                    {t("eventCreation.predictions.globalOpacityLabel")}
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={globalOpacity}
                    onChange={(e) => setAllContoursOpacity(e.target.value)}
                    style={{ width: "100px", height: "4px" }}
                    data-testid="results-opacity-slider"
                  />
                  <span
                    data-testid="results-opacity-value"
                    style={{
                      fontSize: "12px",
                      color: "var(--text-color)",
                      minWidth: "35px",
                    }}
                  >
                    {globalOpacity}%
                  </span>
                </div>

                <IconButton
                  data-testid="results-building-visibility-toggle"
                  onClick={toggleAllBuildings}
                  disabled={buildingsLoading || !anyBuildingExists}
                  style={{
                    backgroundColor: anyBuildingVisible ? "#919090" : "#b10511",
                    color: anyBuildingExists ? "#fff" : "#aaa",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    width: "auto",
                    opacity: anyBuildingExists ? 1 : 0.4,
                  }}
                  size="small"
                >
                  {anyBuildingVisible ? (
                    <DomainDisabledIcon fontSize="small" />
                  ) : (
                    <DomainIcon fontSize="small" />
                  )}
                </IconButton>
              </div>

              {/* Contours list */}
              <div
                className="contours-scroll-container"
                data-testid="results-fire-progression-timeline"
              >
                <div className="pred-legend">
                  {groupedContours.map((group, groupIdx) => {
                    const hourKey = group.hourLabel;
                    const isCollapsed = collapsedHours[hourKey] !== false; // default collapsed
                    const is1hStep = timeStep === "1h";

                    return (
                      <div
                        key={groupIdx}
                        className="hour-group"
                        data-testid={`results-hour-group`}
                      >
                        {/* Hour header row */}
                        <div
                          className="hour-group-header"
                          data-testid="results-contour-hour-toggle"
                          onClick={() =>
                            !is1hStep && toggleHourCollapse(hourKey)
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            cursor: is1hStep ? "default" : "pointer",
                            backgroundColor:
                              "var(--card-bg, rgba(255,255,255,0.05))",
                            marginBottom: "4px",
                            border: `2px solid ${group.color}`,
                          }}
                        >
                          {/* Left: color badge + hour label */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                width: "14px",
                                height: "14px",
                                borderRadius: "3px",
                                background: group.color,
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "var(--text-color)",
                              }}
                            >
                              {group.hourLabel}
                            </span>
                            {/* Contour visibility toggle */}
                            <div>
                              <IconButton
                                size="small"
                                data-testid={`results-contour-visibility-toggle`}
                                 title={t(
                                  "eventCreation.predictions.toggleContourVisibilityTitle",
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  group.items.forEach((c) =>
                                    toggleContourVisibility(c.contourIndex + 1),
                                  );
                                }}
                                style={{
                                  padding: "2px",
                                  color: group.items.some(
                                    (c) =>
                                      geoJsonResults[c.contourIndex]
                                        ?.showContour !== false,
                                  )
                                    ? group.color
                                    : "var(--text-muted, #888)",
                                }}
                              >
                                {group.items.some(
                                  (c) =>
                                    geoJsonResults[c.contourIndex]
                                      ?.showContour !== false,
                                ) ? (
                                  <VisibilityIcon fontSize="small" />
                                ) : (
                                  <VisibilityOffIcon fontSize="small" />
                                )}
                              </IconButton>{" "}
                            </div>
                            {/* {!is1hStep && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  color: "var(--text-muted, #888)",
                                }}
                              >
                                ({group.items.length} contours)
                              </span> 
                            )}*/}
                          </div>

                          {/* Right: cumulative area + total buildings + chevron */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <div
                              className="contour-area-chip"
                              style={{ padding: "2px 8px" }}
                            >
                              <span
                                className="contour-area-value"
                                data-testid={`results-contour-area`}
                                style={{ fontSize: "12px" }}
                              >
                                {group.totalSurface}
                              </span>
                              <span
                                className="contour-area-unit"
                                style={{ fontSize: "10px" }}
                              >
                                {t("eventCreation.predictions.hectareUnit")}
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "11px",
                                color: "var(--text-color)",
                              }}
                              data-testid={`results-contour-buildings-count`}
                            >
                              {buildingsLoading ? (
                                <CircularProgress
                                  size={10}
                                  style={{ color: "#666" }}
                                />
                              ) : (
                                `${group.totalBuildings} ${t("eventCreation.predictions.buildingsAbbrev")}`
                              )}
                            </span>
                            {is1hStep && group.items[0] ? (
                              <label
                                className="contour-toggle-label"
                                style={{ margin: 0 }}
                                data-testid={`results-hour-building-toggle`}
                              >
                                <input
                                  type="checkbox"
                                  className="contour-toggle-input"
                                  checked={
                                    geoJsonResults[group.items[0].contourIndex]
                                      ?.showBuildings || false
                                  }
                                  disabled={
                                    buildingsLoading ||
                                    !geoJsonResults[group.items[0].contourIndex]
                                      ?.building ||
                                    (geoJsonResults[group.items[0].contourIndex]
                                      ?.countBuildings ?? 0) === 0
                                  }
                                  onChange={() =>
                                    toggleContourBuildingsVisibility(
                                      group.items[0].contourIndex + 1,
                                    )
                                  }
                                />
                                <span
                                  className="contour-toggle-track"
                                  style={{ "--toggle-color": group.color }}
                                >
                                  <span className="contour-toggle-thumb" />
                                </span>
                              </label>
                            ) : (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "var(--text-muted, #888)",
                                }}
                              >
                                {isCollapsed ? "▶" : "▼"}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Sub-contour rows — shown when expanded */}
                        {!is1hStep && !isCollapsed && (
                          <div
                            style={{
                              marginLeft: "12px",
                              borderLeft: `3px solid ${group.color}`,
                              paddingLeft: "8px",
                              marginBottom: "6px",
                            }}
                          >
                            {group.items.map((contour, itemIdx) => (
                              <div
                                key={itemIdx}
                                className="contour-card"
                                data-testid={`results-sub-contour`}
                                style={{ marginBottom: "4px" }}
                              >
                                <div
                                  className="contour-card-header"
                                  style={{
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                  }}
                                >
                                  {/* Time label */}
                                  <span
                                    className="contour-label"
                                    style={{
                                      fontSize: "11px",
                                      color: "var(--text-color)",
                                    }}
                                  >
                                    {contour.label}
                                  </span>

                                  {/* Area + buildings + toggle */}
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                    }}
                                  >
                                    <div
                                      className="contour-area-chip"
                                      style={{ padding: "2px 6px" }}
                                    >
                                      <span
                                        className="contour-area-value"
                                        style={{ fontSize: "11px" }}
                                      >
                                        {contour.surface}
                                      </span>
                                      <span
                                        className="contour-area-unit"
                                        style={{ fontSize: "9px" }}
                                      >
                                        ha
                                      </span>
                                    </div>
                                    <div
                                      className="contour-buildings-row"
                                      style={{ margin: 0 }}
                                    >
                                      <label className="contour-toggle-label">
                                        <input
                                          type="checkbox"
                                          className="contour-toggle-input"
                                          checked={
                                            geoJsonResults[contour.contourIndex]
                                              ?.showBuildings || false
                                          }
                                          disabled={
                                            buildingsLoading ||
                                            !geoJsonResults[
                                              contour.contourIndex
                                            ]?.building ||
                                            (geoJsonResults[
                                              contour.contourIndex
                                            ]?.countBuildings ?? 0) === 0
                                          }
                                          onChange={() =>
                                            toggleContourBuildingsVisibility(
                                              contour.contourIndex + 1,
                                            )
                                          }
                                        />
                                        <span
                                          className="contour-toggle-track"
                                          style={{
                                            "--toggle-color": contour.color,
                                          }}
                                        >
                                          <span className="contour-toggle-thumb" />
                                        </span>
                                      </label>
                                      <span
                                        style={{
                                          fontSize: "10px",
                                          color: "var(--text-color)",
                                        }}
                                      >
                                        {buildingsLoading ? (
                                          <CircularProgress
                                            size={8}
                                            style={{ color: "#666" }}
                                          />
                                        ) : (
                                          `${contour.buildingscount} ${t("eventCreation.predictions.buildingsAbbrev")}`
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Pending indicator */}
                  {showPregressBar &&
                    contours.length < expectedContourCount && (
                      <div className="contour-card contour-card--pending">
                        <div
                          className="fire-loader-bar-track"
                          style={{ margin: "8px 0" }}
                        >
                          <div className="fire-loader-bar-fill">
                            <div className="fire-loader-bar-shimmer" />
                          </div>
                        </div>
                        <p
                          className="fire-loader-title"
                          style={{ fontSize: "12px", margin: 0 }}
                        >
                         {t("eventCreation.predictions.computingContour", {
                            label: formatContourLabel(
                              contours.length + 1,
                              dtMinutes,
                            ),
                          })}
                        </p>
                      </div>
                    )}
                </div>
              </div>
              {allDone && (
                <div className="predictions-footer-buttons">
                  <Button
                    type="button"
                    className="sidebar-secondary-btn"
                    onClick={() => setShowStatisticsModal(true)}
                    disabled={contours.length === 0}
                    data-testid="results-statistics-button"
                  >
                    <BarChartIcon style={{ fontSize: 15 }} />
                                        {t("eventCreation.predictions.statistics")}

                  </Button>
                  <Button
                    className="sidebar-secondary-btn"
                    onClick={handleOpenUserHistoric}
                    disabled={!canAccessHistoric}
                    style={{
                      width: "100%",
                      margin: 0,
                      opacity: canAccessHistoric ? 1 : 0.4,
                      cursor: canAccessHistoric ? "pointer" : "not-allowed",
                    }}
                    data-testid="results-view-history-button"
                  >
                    {t("eventCreation.predictions.viewHistory")}
                  </Button>
                  <ExportDropdown
                    onExport={handleExport}
                    disabled={contours.length === 0}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Collapse>
      <StatisticsModal
        open={showStatisticsModal}
        onClose={() => setShowStatisticsModal(false)}
        geoJsonResults={geoJsonResults}
        geoJsonInputContour={geoJsonInputContour}
        predictionHours={predictionHours}
        timeStep={timeStep}
        isLightMode={isLightMode}
      />
    </div>
  );
};

export default FirePredictions;
